import { createRouter, createWebHistory } from 'vue-router'

import { authGuard } from '@/auth/auth-guard'
import LandingPage from '@/pages/LandingPage.vue'
import { captureException } from '@/sentry'
import { useBreadcrumbsStore } from '@/stores/breadcrumbs-store'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: LandingPage
    },
    {
      path: '/dashboard',
      component: () => import('@/pages/DashboardPage.vue'),
      meta: {
        requiresRole: 'user'
      }
    },
    {
      path: '/my-account',
      component: () => import('@/pages/MyAccountPage.vue'),
      meta: {
        breadcrumb: () => (Promise.resolve({ text: 'Mein Konto', to: '/my-account' }))
      }
    },
    {
      path: '/my-company',
      component: () => import('@/pages/MyCompanyPage.vue'),
      meta: {
        breadcrumb: () => (Promise.resolve({ text: 'Meine Firma', to: '/my-company' }))
      }
    },
    {
      path: '/cars',
      component: () => import('@/pages/CarsPage.vue'),
      meta: {
        requiresRole: 'user',
        breadcrumb: () => (Promise.resolve({ text: 'Fahrzeuge', to: '/cars' }))
      }
    },
    {
      path: '/drafts',
      component: () => import('@/pages/CarDraftsPage.vue'),
      meta: {
        requiresRole: 'user',
        breadcrumb: () => Promise.resolve({ text: 'Entwürfe', to: '/drafts' })
      }
    },
    {
      path: '/drafts/:id',
      component: () => import('@/pages/NewCarPage.vue'),
      meta: {
        requiresRole: 'user',
        parentComponent: (router) => router.resolve('/drafts'),
        breadcrumb: async (router): Promise<{ text: string; to: string }> => {
          const route = router.currentRoute.value
          const carDraftId = Array.isArray(route.params.id) ? route.params.id[0]! : route.params.id!
          try {
            const breadcrumbsStore = useBreadcrumbsStore()
            const title = await breadcrumbsStore.fetchDraftTitle(carDraftId)
            return { text: title, to: `/drafts/${carDraftId}` }
          } catch (err: unknown) {
            captureException(err, { extra: { id: carDraftId } })
            return { text: carDraftId, to: `/drafts/${carDraftId}` }
          }
        }
      }
    },
    {
      path: '/cars/new',
      component: () => import('@/pages/NewCarPage.vue'),
      meta: {
        requiresRole: 'user',
        parentComponent: (router) => router.resolve('/cars'),
        breadcrumb: () => (Promise.resolve({ text: 'Neues Fahrzeug', to: '/cars/new' }))
      }
    },
    {
      path: '/cars/:carId/edit',
      component: () => import('@/pages/EditCarPage.vue'),
      meta: {
        requiresRole: 'user',
        parentComponent: (router) => {
          const route = router.currentRoute.value
          const carId = Array.isArray(route.params.carId) ? route.params.carId[0] : route.params.carId
          return router.resolve(`/cars/${carId}`)
        },
        breadcrumb: (router) => {
          const route = router.currentRoute.value
          const carId = Array.isArray(route.params.carId) ? route.params.carId[0] : route.params.carId
          return Promise.resolve({ text: 'Bearbeiten', to: `/cars/${carId}/edit` })
        }
      }
    },
    {
      path: '/login',
      component: () => import('@/pages/LoginPage.vue')
    },
    {
      path: '/reset-password',
      component: () => import('@/pages/ResetPasswordPage.vue')
    },
    {
      path: '/reset-password/success',
      component: () => import('@/pages/ResetPasswordSuccessPage.vue')
    },
    {
      path: '/reset-password/request',
      component: () => import('@/pages/ResetPasswordRequestPage.vue')
    },
    {
      path: '/reset-password/request-success',
      component: () => import('@/pages/ResetPasswordRequestSuccessPage.vue')
    },
    {
      path: '/signup',
      component: () => import('@/pages/SignupPage.vue')
    },
    {
      path: '/signup/success',
      component: () => import('@/pages/SignupSuccessPage.vue')
    },
    {
      path: '/confirm-email/success',
      component: () => import('@/pages/ConfirmEmailSuccessPage.vue')
    },
    {
      path: '/change-email/success',
      component: () => import('@/pages/ChangeEmailSuccessPage.vue')
    },
    {
      path: '/confirm-email/sent',
      component: () => import('@/pages/ConfirmEmailSendSuccessPage.vue')
    },
    {
      path: '/confirm-email/resend',
      component: () => import('@/pages/ConfirmEmailResendPage.vue')
    },
    {
      path: '/user-management',
      component: () => import('@/pages/UserManagementPage.vue'),
      beforeEnter: (to, _, next) => {
        if (to.path === '/user-management') next('/user-management/users')
        else next()
      },
      meta: {
        requiresRole: 'admin',
        breadcrumb: () => Promise.resolve({ text: 'Benutzerverwaltung', to: '/user-management' })
      },
      children: [
        {
          path: 'signups',
          component: () => import('@/users/SignupsView.vue'),
          meta: {
            parentComponent: (router) => router.resolve('/user-management'),
            breadcrumb: () => Promise.resolve({ text: 'Registrierungen', to: '/user-management/signups' })
          }
        },
        {
          path: 'users',
          component: () => import('@/users/UsersView.vue'),
          meta: {
            parentComponent: (router) => router.resolve('/user-management'),
            breadcrumb: () => Promise.resolve({ text: 'Benutzer', to: '/user-management/users' })
          }
        }
      ]
    },
    {
      path: '/user-feedback',
      component: () => import('@/pages/UserFeedbackPage.vue'),
      meta: {
        requiresRole: 'admin',
        breadcrumb: () => Promise.resolve({ text: 'Nutzerfeedback', to: '/user-feedback' })
      }
    },
    {
      path: '/cars/:carId',
      component: () => import('@/pages/CarDetailPage.vue'),
      props: true,
      meta: {
        requiresRole: 'user',
        parentComponent: (router) => router.resolve('/cars'),
        breadcrumb: async (router): Promise<{ text: string; to: string }> => {
          const route = router.currentRoute.value
          const carId = Array.isArray(route.params.carId) ? route.params.carId[0]! : route.params.carId!
          try {
            const breadcrumbsStore = useBreadcrumbsStore()
            const carTitle = await breadcrumbsStore.fetchCarTitle(carId)
            return { text: carTitle, to: `/cars/${carId}` }
          } catch (err: unknown) {
            captureException(err, { extra: { carId } })
            return { text: carId, to: `/cars/${carId}` }
          }
        }
      }
    },
    {
      path: '/feedback',
      component: () => import('@/pages/FeedbackPage.vue')
    },
    {
      path: '/feedback/sent-success',
      component: () => import('@/pages/FeedbackSentSuccessPage.vue')
    },
    {
      path: '/my-network',
      component: () => import('@/pages/MyNetworkPage.vue'),
      beforeEnter: (to, _, next) => {
        if (to.path === '/my-network') next('/my-network/connections')
        else next()
      },
      meta: {
        requiresRole: 'user',
        breadcrumb: () => Promise.resolve({ text: 'Mein Netzwerk', to: '/my-network' })
      },
      children: [
        {
          path: 'connections',
          component: () => import('@/components/network/ConnectionsView.vue')
        },
        {
          path: 'requests',
          component: () => import('@/components/network/RequestsView.vue')
        }
      ]
    },
    {
      path: '/companies',
      component: () => import('@/pages/CompaniesPage.vue'),
      meta: {
        requiresRole: 'user',
        breadcrumb: () => Promise.resolve({ text: 'Firmen', to: '/companies' })
      }
    },
    {
      path: '/companies/:companyId',
      component: () => import('@/pages/CompanyDetailPage.vue'),
      props: true,
      meta: {
        requiresRole: 'user',
        parentComponent: (router) => router.resolve('/companies'),
        breadcrumb: async (router): Promise<{ text: string; to: string }> => {
          const route = router.currentRoute.value
          const companyId = Array.isArray(route.params.companyId) ? route.params.companyId[0]! : route.params.companyId!
          try {
            const breadcrumbsStore = useBreadcrumbsStore()
            const companyName = await breadcrumbsStore.fetchCompanyName(companyId)
            return { text: companyName, to: `/companies/${companyId}` }
          } catch (err: unknown) {
            captureException(err, { extra: { companyId } })
            return { text: companyId, to: `/companies/${companyId}` }
          }
        }
      }
    },
    {
      path: '/company-management',
      component: () => import('@/pages/CompanyManagementPage.vue'),
      meta: {
        requiresRole: 'admin',
        breadcrumb: () => Promise.resolve({ text: 'Firmenverwaltung', to: '/company-management' })
      }
    },
    {
      path: '/company-management/new',
      component: () => import('@/pages/NewCompanyPage.vue'),
      meta: {
        requiresRole: 'admin',
        parentComponent: (router) => router.resolve('/company-management'),
        breadcrumb: () => Promise.resolve({ text: 'Neue Firma', to: '/company-management/new' })
      }
    },
    {
      path: '/forbidden',
      component: () => import('@/pages/ForbiddenPage.vue')
    },
    {
      path: '/not-found',
      component: () => import('@/pages/NotFoundPage.vue')
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/not-found'
    }
  ]
})

router.beforeEach(authGuard)

// Clean table query params when navigating away from a route
// This runs AFTER navigation completes, avoiding race conditions with logout
const TABLE_QUERY_PARAMS = ['sort', 'ascending', 'limit', 'page']

router.afterEach((to, from) => {
  // Only clean if we navigated to a different route
  if (to.path === from.path) return

  // Check if the current URL still has table params from the previous route
  const hasTableParams = TABLE_QUERY_PARAMS.some(param => to.query[param] !== undefined)
  if (!hasTableParams) return

  // Remove table params from the URL
  const cleanQuery = { ...to.query }
  TABLE_QUERY_PARAMS.forEach(param => delete cleanQuery[param])

  void router.replace({ query: cleanQuery })
})

export default router
